import { useEffect, useMemo, useState } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import axios from "axios";
import "@/App.css";
import { FREE_SHIRT_PROMO_START_ISO, FREE_SHIRT_THRESHOLD, PRODUCTS } from "@/data/products";
import TacticalHeader from "@/components/TacticalHeader";
import KineticHero from "@/components/KineticHero";
import EditorialMarquee from "@/components/EditorialMarquee";
import ArmoryCatalog from "@/components/ArmoryCatalog";
import SupplyChainNote from "@/components/SupplyChainNote";
import Manifesto from "@/components/Manifesto";
import TacticalFooter from "@/components/TacticalFooter";
import CartDrawer from "@/components/CartDrawer";
import InspectModal from "@/components/InspectModal";
import CheckoutModal from "@/components/CheckoutModal";
import PaymentResult from "@/components/PaymentResult";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FREE_SHIRT_NAME = "Signal Support Heavyweight Tee";

function Home() {
  const [cart, setCart] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [inspectProduct, setInspectProduct] = useState(null);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const cartItems = useMemo(() => cart.map((line) => ({ ...line, product: PRODUCTS.find((product) => product.id === line.productId) })).filter((line) => line.product), [cart]);
  const cartCount = cart.reduce((sum, line) => sum + line.quantity, 0);
  const cartTotal = cartItems.reduce((sum, line) => sum + line.product.price * line.quantity, 0);
  const promotionActive = Date.now() >= Date.parse(FREE_SHIRT_PROMO_START_ISO);
  const freeShirtUnlocked = promotionActive && cartTotal >= FREE_SHIRT_THRESHOLD;
  const freeShirtRemaining = Math.max(0, FREE_SHIRT_THRESHOLD - cartTotal);

  useEffect(() => {
    document.body.classList.toggle("modal-open", drawerOpen || checkoutOpen || Boolean(inspectProduct));
    return () => document.body.classList.remove("modal-open");
  }, [drawerOpen, checkoutOpen, inspectProduct]);

  const addToCart = (product) => {
    setCart((current) => current.some((line) => line.productId === product.id) ? current.map((line) => line.productId === product.id ? { ...line, quantity: line.quantity + 1 } : line) : [...current, { productId: product.id, quantity: 1 }]);
    setInspectProduct(null);
    setDrawerOpen(true);
  };
  const updateQuantity = (productId, quantity) => setCart((current) => current.map((line) => line.productId === productId ? { ...line, quantity: Math.max(1, quantity) } : line));
  const removeFromCart = (productId) => setCart((current) => current.filter((line) => line.productId !== productId));
  const openCheckout = () => { setDrawerOpen(false); setCheckoutError(""); setCheckoutOpen(true); };
  const authorizeCheckout = async () => {
    setCheckoutBusy(true);
    setCheckoutError("");
    try {
      const response = await axios.post(`${API}/payments/checkout`, { items: cartItems.map((line) => ({ lookup_key: line.product.lookupKey, quantity: line.quantity })), origin_url: window.location.origin });
      window.location.assign(response.data.checkout_url);
    } catch (error) {
      setCheckoutError(error.response?.data?.detail || "Secure checkout is unavailable. Please try again.");
      setCheckoutBusy(false);
    }
  };
  return (
    <div className="app-shell">
      <TacticalHeader cartCount={cartCount} onCart={() => setDrawerOpen(true)} />
      <main>
        <KineticHero onExplore={() => document.getElementById("armory")?.scrollIntoView({ behavior: "smooth" })} onLedger={() => document.getElementById("pledge")?.scrollIntoView({ behavior: "smooth" })} />
        <EditorialMarquee />
        <ArmoryCatalog products={PRODUCTS} onAdd={addToCart} onInspect={setInspectProduct} />
        <SupplyChainNote />
        <Manifesto cartTotal={cartTotal} />
      </main>
      <TacticalFooter />
      <CartDrawer open={drawerOpen} items={cartItems} total={cartTotal} freeShirtUnlocked={freeShirtUnlocked} freeShirtRemaining={freeShirtRemaining} promotionActive={promotionActive} onClose={() => setDrawerOpen(false)} onQuantity={updateQuantity} onRemove={removeFromCart} onCheckout={openCheckout} />
      <InspectModal product={inspectProduct} onClose={() => setInspectProduct(null)} onAdd={addToCart} />
      <CheckoutModal open={checkoutOpen} items={cartItems} total={cartTotal} freeShirtUnlocked={freeShirtUnlocked} freeShirtName={FREE_SHIRT_NAME} promotionActive={promotionActive} busy={checkoutBusy} error={checkoutError} onClose={() => setCheckoutOpen(false)} onAuthorize={authorizeCheckout} />
    </div>
  );
}

function App() {
  return <BrowserRouter><Routes><Route path="/" element={<Home />} /><Route path="/payment/success" element={<PaymentResult />} /><Route path="/payment/cancel" element={<PaymentResult cancelled />} /></Routes></BrowserRouter>;
}

export default App;
